import {AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {PaginatorService} from "@shared-services/paginator.service";
import {UtilsService} from "@shared-services/utils.service";
import {ErrorReportLine, ErrorSummaryLine, PaymentReportLine, TotalTradingReport} from "../trading-report.interface";
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';

@Component({
  selector: 'app-error-trading-report',
  templateUrl: './error-trading-report.component.html',
  styleUrls: ['./error-trading-report.component.scss']
})
export class ErrorTradingReportComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('paginator1') paginator1!: MatPaginator;
  @ViewChild('paginator2') paginator2!: MatPaginator;
  @ViewChild('sort1') sort1!: MatSort;
  @ViewChild('sort2') sort2!: MatSort;
  @Input() totalTradingReport!: TotalTradingReport;
  @Input() paymentMethodType!: 'OFFERING' | 'SHARED' | null;

  summaryColumns: string[] = ['transactionDate', 'paymentMethod', 'amountReceived', 'errorCount'];
  summaryExportModel = [
    {key:'transactionDate', displayName: 'Transaction Date'},
    {key:'paymentMethod', displayName: 'Payment Method'},
    {key:'amountReceived', displayName: 'Amount Received'}
  ]
  summaryTableTitle = 'Amount per day which could not be linked to a tenant';
  reportColumns: string[] = ['transactionDate', 'transactionInvoiceNumber', 'productName', 'productSku', 'amountReceived'];
  reportExportModel = [
    {key:'transactionDate', displayName: 'Transaction Date'},
    {key: 'transactionInvoiceNumber', displayName: 'Transaction Invoice Number'},
    {key: 'productName', displayName: 'Product Name'},
    {key: 'productSku', displayName: 'Product SKU'},
    {key: 'amountReceived', displayName: 'Amount Received'}
  ]
  reportTableTitle = 'Sales which could not be linked to a tenant';
  errorSummaryDatasource = new MatTableDataSource<ErrorSummaryLine>();
  errorReportDatasource = new MatTableDataSource<ErrorReportLine>();

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private paginatorService: PaginatorService,
              public loader: LoadingService,
              ) {
    this.setFilterPredicate();
  }

  ngOnInit() {
    this.setReportData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalTradingReport']) {
      this.setReportData();
    }
  }

  ngAfterViewInit() {
    this.paginator1._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    this.paginator2._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;

    this.errorSummaryDatasource.paginator = this.paginator1;
    this.errorReportDatasource.paginator = this.paginator2;

    this.errorSummaryDatasource.sort = this.sort1;
    this.errorReportDatasource.sort = this.sort2;
  }

  setReportData() {
    this.errorSummaryDatasource.data = [ ...this.totalTradingReport.errorSummaryReport ];
    this.errorReportDatasource.data = [ ...this.totalTradingReport.errorTradingReport ];
  }

  setFilterPredicate() {
    this.errorSummaryDatasource.filterPredicate = (data: PaymentReportLine, filter: string) => {
      const filterValues = JSON.parse(filter);
      const matchFilter = [];

      const paymentMethodMatch = data.paymentMethod.toLowerCase().includes(filterValues.paymentMethod.toLowerCase());
      matchFilter.push(paymentMethodMatch);
      return matchFilter.every(Boolean);
    };
  }

  applyFilter(formValues: any) {
    const filterValues = {
      paymentMethod: formValues.paymentMethod || ''
    };
    this.errorSummaryDatasource.filter = JSON.stringify(filterValues);

    if (this.errorSummaryDatasource.paginator) {
      this.errorSummaryDatasource.paginator.firstPage();
    }
  }

  getSummaryAmountReceivedTotal (): number {
    return this.errorSummaryDatasource.filteredData.map(item => item.amountReceived)
      .reduce((acc, val) => acc + val, 0);
  }
  getErrorCountTotal (): number {
    return this.errorSummaryDatasource.filteredData.map(item => item.errorCount)
      .reduce((acc, val) => acc + val, 0);
  }
  getErrorAmountReceivedTotal (): number {
    return this.errorReportDatasource.filteredData.map(item => item.amountReceived)
      .reduce((acc, val) => acc + val, 0);
  }

  exportToCsv(dataSourceName: string) {
    let datasource;
    let tableTitle;
    let exportModel;
    if (dataSourceName === 'summary') {
      datasource = this.errorSummaryDatasource;
      tableTitle = this.summaryTableTitle;
      exportModel = this.summaryExportModel
    } else {
      datasource = this.errorReportDatasource;
      tableTitle = this.reportTableTitle;
      exportModel = this.reportExportModel;
    }

    const dataWithDisplayLabels = datasource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const csv = Papa.unparse(dataWithDisplayLabels);
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    this.downloadFile(url, `${tableTitle}-${new Date().toISOString()}.csv`);

  }

  exportToExcel(dataSourceName: string) {

    let datasource;
    let tableTitle;
    let exportModel;
    if (dataSourceName === 'summary') {
      datasource = this.errorSummaryDatasource;
      tableTitle = this.summaryTableTitle;
      exportModel = this.summaryExportModel
    } else {
      datasource = this.errorReportDatasource;
      tableTitle = this.reportTableTitle;
      exportModel = this.reportExportModel;
    }
    const dataWithDisplayLabels = datasource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataWithDisplayLabels);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${tableTitle}-${new Date().toISOString()}.xlsx`);
  }

  exportToPdf(dataSourceName: string) {

    let datasource;
    let tableTitle;
    let exportModel;
    if (dataSourceName === 'summary') {
      datasource = this.errorSummaryDatasource;
      tableTitle = this.summaryTableTitle;
      exportModel = this.summaryExportModel
    } else {
      datasource = this.errorReportDatasource;
      tableTitle = this.reportTableTitle;
      exportModel = this.reportExportModel;
    }

    const doc = new jsPDF('landscape', 'pt', 'a4');
    const columns = exportModel.map((col) => col.displayName);
    const body = datasource.filteredData.map((row: any) =>
      exportModel.map((col) => row[col.key]?.toString() || '')
    );
    autoTable(doc, {
      head: [columns],
      body: body,
    });
    doc.save(`${tableTitle}-${new Date().toISOString()}.pdf`);

  }

  private downloadFile(url: string, fileName: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
